import React from 'react';
import { FolderOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FilePickerButtonProps {
  label: string;
  accept: string;
  disabled?: boolean;
  onSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * The `<label>` wrapping the hidden input IS the picker: nothing calls .click()
 * on the input, so a plain button here selects no files and reports no error.
 *
 * role/tabIndex/keydown reproduce what MUI's ButtonBase added for
 * `component="label"` — without them the control drops off the keyboard, since
 * a bare label is neither focusable nor activated by Enter.
 */
const FilePickerButton: React.FC<FilePickerButtonProps> = ({
  label,
  accept,
  disabled = false,
  onSelect,
}) => (
  <Button
    asChild
    variant="outline"
    // The island's cursor:pointer rule targets <button>; this one is a label.
    className={cn('cursor-pointer', disabled && 'pointer-events-none opacity-50')}
  >
    <label
      role="button"
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        event.currentTarget.querySelector('input')?.click();
      }}
    >
      <FolderOpen strokeWidth={1.9} />
      <span className="revy-cjk">{label}</span>
      <input type="file" multiple accept={accept} hidden onChange={onSelect} />
    </label>
  </Button>
);

export default FilePickerButton;
